'use client';
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    useTransition,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';
import { FilterableGridProps } from './collection-filterable-grid.d';
import { useInView } from 'react-intersection-observer';
import {
    FilteringToolbarCallbacks,
    ProductPhotoVariant,
    Sort,
    View,
} from '@app/[lang]/collections/collection-filtering-toolbar/types';
import { trimGID } from '@app/lib/shopify/helpers/gid';
import { Drawer, DrawerPanel } from '@app/components/ui/drawer/Drawer';
import ManagedFiltersDrawer from '../managed-filters-drawer/managed-filters-drawer';
import connectShopify from '@app/components/product-card/connect-shopify';
import CollectionFilteringToolbar from '@app/[lang]/collections/collection-filtering-toolbar/collection-fiiltering-toolbar';
import { ShopifyFilter } from '@app/lib/shopify/types/Filter';
import { ProductCard } from 'mmds';
import { collectionByHandle, getClient } from '@app/lib/shopify';
import useUI from '@app/components/ui/state';
import useDrawerDirection from '@app/lib/hooks/use-drawer-direction';
import { Slice } from '@prismicio/client';
import PosterWithPrismic from '@app/components/poster/withPrismic';
import { NostoPosterPlacementSlice, PosterSlice } from 'prismicio-types';
import { Language } from '@app/lib/store-context/Context';
import productImpressionGTMEvent, {
    ProductImpressionEvent,
} from '@app/lib/events/product-impression.gtm';
import useCollection from '@app/[lang]/collections/state';
import productsByQuery from '@app/lib/shopify/queries/products-by-query';
import DefaultCollectionFilters from '@app/lib/shopify/filters/default-collection-filters';
import NostoDisruptorPoster from '@app/slices/NostoDisruptorPoster';
import removeQueryParams from '@app/lib/urls/remove-query-params';
import CollectionNostoTracking from '@app/components/nosto-tracking/CollectionNostoTracking';
import cn from 'clsx';
import s from './collection-filterable-grid.module.css';

// Used to record which products have already sent a GTM
// product impression to prevent duplicate impressions.
// Must remain out of component function scope otherwise
// resets on render.
const trackedImpressions: string[] = [];

const QUERY_MODE_COLLECTION = 'COLLECTION';
const QUERY_MODE_SEARCH = 'SEARCH';

const CollectionFilterableGrid = ({
    collection,
    clientSettings,
    totalProductCount,
    prismicDocument,
    term,
}: FilterableGridProps) => {
    const ref = useRef();
    const searchParams = useSearchParams();
    const [, startTransition] = useTransition();
    const { ref: loadMoreTriggerRef, inView: loadMoreIsInView } = useInView({
        rootMargin: '2000px',
    });
    const isMountingRef = useRef(true);
    const isLoadingMore = useRef(false);
    const shouldRefreshParams = !!searchParams.get('navigation');

    let queryMode = QUERY_MODE_COLLECTION;
    if (term) {
        queryMode = QUERY_MODE_SEARCH;
    }

    let query = term ? decodeURI(term).split(' ').join(' AND ') : null;
    const graphQLQuery =
        queryMode === QUERY_MODE_COLLECTION
            ? collectionByHandle
            : productsByQuery;

    const {
        products,
        productCount,
        productCursor,
        hasNextPage,
        setProducts,
        addProducts,
        setSelectedSort,
        setSelectedFilters,
        setCurrentCollectionHandle,
        appliedFilters,
        initialiseFromQueryParams,
        resetFilters,
    } = useCollection();

    const {
        isStateInitialised,
        selectedFilters,
        selectedSort,
        availableFilters,
        currentCollectionHandle,
    } = useCollection(
        useShallow((state) => ({
            isStateInitialised: state.isStateInitialised,
            selectedFilters: state.selectedFilters,
            selectedSort: state.selectedSort,
            availableFilters: state.availableFilters,
            currentCollectionHandle: state.currentCollectionHandle,
        }))
    );

    useEffect(() => {
        if (currentCollectionHandle !== collection.handle) {
            setProducts(collection.products);
            setCurrentCollectionHandle(collection.handle);
            resetFilters();
            initialiseFromQueryParams();
        }
    }, [collection]);

    // Fixes an issue where collections don't refresh when linking
    // to the same collection with a new filter.
    useEffect(() => {
        if (shouldRefreshParams) {
            initialiseFromQueryParams();
            removeQueryParams(['navigation']);
        }
    }, [shouldRefreshParams]);

    const [productPhotoVariant, setProductPhotoVariant] =
        useState<ProductPhotoVariant>('flatlay');

    const filterView: View = selectedFilters.length ? 'activeFilters' : 'count';
    const filteringCallbacks: FilteringToolbarCallbacks = {
        selectProductPhotoVariant: (variant) => setProductPhotoVariant(variant),
        toggleFilteringUI: () => toggleUI('collection-filters'),
        applyFilters: (filters: ShopifyFilter[]) => {
            setSelectedFilters(filters);
        },
        applySort: (sort: Sort): void => {
            setSelectedSort(sort);
        },
        clearFilters: () => setSelectedFilters([]),
    };

    const { openUIKey, toggleUI } = useUI();
    const isDrawerOpen = openUIKey === 'collection-filters';
    const drawerDirection = useDrawerDirection();

    useEffect(() => {
        if (!isStateInitialised) return;
        // Avoid re-fetching the products on first load if no custom filters have been applied
        let isMounted = true;
        let shouldUpdate: boolean = true;
        if (isMountingRef.current && !selectedFilters.length && !selectedSort) {
            shouldUpdate = false;
        }
        isMountingRef.current = false;
        if (!shouldUpdate) return;
        //----

        let pageSize = 34;
        if (selectedFilters.length) {
            pageSize = 250;
        }

        getClient(clientSettings)
            .request.send({
                query: graphQLQuery,
                variables: {
                    country: clientSettings.shopifyMarketCountryCode,
                    query,
                    handle: collection.handle,
                    first: pageSize,
                    filters: appliedFilters,
                    sortKey: selectedSort?.sortKey,
                    reverse: selectedSort?.reverse,
                },
            })
            .then((response) => {
                if (isMounted && response) {
                    startTransition(() => {
                        if (queryMode === QUERY_MODE_COLLECTION) {
                            setProducts(response.collection.products);
                        } else {
                            setProducts({
                                edges: response.search.edges,
                                pageInfo: response.search.pageInfo,
                                filters: response.search.productFilters,
                            });
                        }
                    });
                }
            });

        return () => {
            isMounted = false;
        };
    }, [selectedFilters, appliedFilters, selectedSort]);

    let disruptorSlices: Slice[] = [];
    if (prismicDocument && prismicDocument.data) {
        disruptorSlices = prismicDocument.data.slices4.filter(
            (slice) =>
                ['poster', 'nosto_poster_placement'].includes(
                    slice.slice_type
                ) &&
                (slice.items.length || slice.primary.grid_position)
        );
    }

    const setRefs = useCallback(
        (node) => {
            ref.current = node;
            loadMoreTriggerRef(node);
        },
        [loadMoreTriggerRef]
    );

    useEffect(() => {
        if (hasNextPage && loadMoreIsInView && !isLoadingMore.current) {
            isLoadingMore.current = true;
            const filtersToApply = [
                ...appliedFilters,
                ...DefaultCollectionFilters,
            ];
            getClient(clientSettings)
                .request.send({
                    query: graphQLQuery,
                    variables: {
                        country: clientSettings.shopifyMarketCountryCode,
                        query,
                        handle: collection.handle,
                        first: selectedFilters.length ? 250 : 34,
                        filters: filtersToApply,
                        sortKey: selectedSort?.sortKey,
                        reverse: selectedSort?.reverse,
                        after: productCursor,
                    },
                })
                .then((response) => {
                    startTransition(() => {
                        if (queryMode === QUERY_MODE_COLLECTION) {
                            addProducts(response.collection.products);
                        } else {
                            addProducts({
                                edges: response.search.edges,
                                pageInfo: response.search.pageInfo,
                                filters: response.search.productFilters,
                            });
                        }
                        isLoadingMore.current = false;
                    });
                });
        }
    }, [loadMoreIsInView]);
    // On the server-side, the state store will not be initialised, but still need to
    // render the products in HTML, fall-back to the server-side collection.
    let isomorphicProducts = products;
    if (isomorphicProducts.length === 0) {
        isomorphicProducts = collection.products.edges.map(
            (productEdge) => productEdge.node
        );
    }
    let isoMorphicAvailableFilters = availableFilters;
    if (isoMorphicAvailableFilters.length === 0) {
        isoMorphicAvailableFilters = collection.products.filters;
    }

    let dynamicProductCount: string | number = selectedFilters.length
        ? productCount
        : totalProductCount;

    if (dynamicProductCount > 249) {
        dynamicProductCount = '250+';
    }

    // Filter empty object from the products array
    const filteredProducts = isomorphicProducts.filter(
        (item) => Object.keys(item).length !== 0
    );
    return (
        <>
            <CollectionFilteringToolbar
                productCount={dynamicProductCount}
                selectedFilters={selectedFilters}
                selectedProductPhotoVariant={productPhotoVariant}
                currentView={filterView}
                callbacks={filteringCallbacks}
                lang={clientSettings.languageCode as Language}
            />
            <Drawer
                open={isDrawerOpen}
                onOpenChange={() =>
                    toggleUI(isDrawerOpen ? null : 'collection-filters')
                }
            >
                <DrawerPanel inFrom={drawerDirection} variant="filters">
                    <ManagedFiltersDrawer
                        selectedFilters={selectedFilters}
                        selectedSort={selectedSort}
                        availableFilters={isoMorphicAvailableFilters}
                        productCount={dynamicProductCount}
                        callbacks={filteringCallbacks}
                        lang={clientSettings.languageCode as Language}
                    />
                </DrawerPanel>
            </Drawer>
            <div className={s.grid}>
                {filteredProducts?.map((product, index) => {
                    const disruptor = disruptorSlices.find((slice) => {
                        const requestedPosition =
                            slice.items[0]?.grid_position ||
                            slice.primary?.grid_position;
                        return requestedPosition === index + 1;
                    });
                    const ConnectedProductCard = connectShopify(ProductCard);
                    const firstVariant = product.variants.edges[0].node;
                    if (!trackedImpressions.includes(product.id)) {
                        const impression: ProductImpressionEvent = {
                            id: product.id,
                            sku: product.variants.edges[0].node.sku as string,
                            name: product.title,
                            currencyCode: firstVariant.priceV2.currencyCode,
                            price: firstVariant.priceV2.amount,
                            brand: 'MAAP',
                            category: 'Apparel & Accessories',
                            list: collection.title,
                        };
                        if (
                            impression.id ===
                            'gid://shopify/Product/7393537228915'
                        )
                            productImpressionGTMEvent(impression);
                        trackedImpressions.push(product.id);
                    }
                    return (
                        <>
                           {!!disruptor && (
                                <div
                                    className={cn({
                                        [s.double]:
                                            // @ts-ignore
                                            disruptor.variation === 'twoUp',
                                    })}
                                    data-disruptor-id={`disruptor-${index}`}
                                    data-disruptor-collection={
                                        collection.handle
                                    }
                                    key={`disruptor-${index}`}
                                >
                                    {disruptor.slice_type === 'poster' && (
                                        <PosterWithPrismic
                                            slice={disruptor as PosterSlice}
                                            context={{
                                                lang: clientSettings.languageCode,
                                            }}
                                        />
                                    )}
                                    {disruptor.slice_type ===
                                        'nosto_poster_placement' && (
                                            <NostoDisruptorPoster
                                                index={index}
                                                slice={
                                                    disruptor as NostoPosterPlacementSlice
                                                }
                                                slices={disruptorSlices}
                                                context={{
                                                    lang: clientSettings.languageCode,
                                                }}
                                            />
                                        )}
                                </div>
                            )}
                            <div key={product.id}>
                                <div
                                    key={product.id}
                                    data-product-id={trimGID(product.id)}
                                    data-product-handle={product.handle}
                                >
                                    <ConnectedProductCard
                                        product={product}
                                        photoVariant={productPhotoVariant}
                                        position={index}
                                        collectionHandle={collection.handle}
                                        clientSettings={clientSettings}
                                    />
                                </div>
                            </div>
                        </>
                    );
                })}
                <span ref={setRefs}></span>
            </div>
            <CollectionNostoTracking
                collectionId={collection.id}
                collectionHandle={currentCollectionHandle || collection.handle}
                products={filteredProducts}
                term={term}
            />
        </>
    );
};

export default CollectionFilterableGrid;
